/**
 * Discovery Panel Component (issues #61, #62)
 *
 * Sidebar search box that calls `GatewayService.discover()` and renders the
 * response in four expandable sections (Entities, Concepts, Documents, Topics).
 * Resolver source → section mapping is configured in
 * `src/core/app-constants.ts` via DISCOVERY_SOURCE_TO_SECTION.
 *
 * Features:
 * - Submit on Enter or button click
 * - Sends surrounding document text as `context` for disambiguation
 *   (when a document is loaded, via useAppStore)
 * - Disambiguation prompt with clickable alternatives
 * - Click handlers for documents (open in new tab) and topics (extract entities)
 * - Loading and error states; aborts in-flight requests on new query
 * - Collapsible source selector populated by `GatewayService.getSources()`;
 *   if all sources are checked, the discover() call omits the sources filter.
 *   Silent degradation: if getSources() fails, the selector is hidden and
 *   discover() is called without a filter (queries all resolvers).
 * - i18n via the `discovery` namespace with built-in fallbacks
 */

import type GatewayService from '../core/gateway-service';
import type {
  DiscoveryResponse,
  DiscoveryResolverResult,
  DiscoveryDisambiguationResult,
} from '../core/gateway-service';
import {
  DISCOVERY_SECTIONS,
  DISCOVERY_SOURCE_TO_SECTION,
  DISCOVERY_SECTION_DEFAULT,
  type DiscoverySection,
} from '../core/app-constants';
import { useAppStore } from '../stores/app-store';
import ToastService from './toast-service';
import { escapeHtml } from '../core/utilities/dom';
import { BaseComponent, type BaseComponentOptions } from './base-component';
import { logger } from '../core/logger';
import { forbiddenMessage } from '../core/forbidden-message';

// Maximum characters of document content used as disambiguation context.
// Discovery API doesn't need the whole document — a representative slice is
// enough to disambiguate the search term in the document's domain.
const CONTEXT_MAX_CHARS = 2000;

// Inline SVG icons (Bootstrap Icons, MIT-licensed paths) — used in dynamic
// result items rendered by renderResultItem().
const SVG_DOCUMENT = `
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5"/>
    <path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5zM9 1v2a1 1 0 0 0 1 1h3v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z"/>
  </svg>
`;
const SVG_TAG = `
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M6 4.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0"/>
    <path d="M2 1a1 1 0 0 0-1 1v4.586a1 1 0 0 0 .293.707l7 7a1 1 0 0 0 1.414 0l4.586-4.586a1 1 0 0 0 0-1.414l-7-7A1 1 0 0 0 6.586 1zm0 1h4.586L13.586 9 9 13.586 2 6.586z"/>
  </svg>
`;

export interface DiscoveryPanelOptions extends BaseComponentOptions {
  gatewayService: GatewayService;
  /**
   * Optional callback when a document item is clicked. Default: open the
   * document's URL in a new tab if available.
   */
  onDocumentClick?: (doc: unknown) => void;
  /**
   * Optional callback when a topic item is clicked. The wired-up app
   * implementation should trigger entity extraction for the topic.
   */
  /**
   * Optional callback fired when the set of checked topics changes.
   * Receives the current array of selected topic IDs.
   */
  onSelectionChange?: (selectedTopicIds: string[]) => void;
  /**
   * Optional provider for disambiguation context. Called at search time;
   * return null/undefined to omit the `context` parameter. Defaults to
   * reading from useAppStore's currentDocument.content (truncated).
   */
  contextProvider?: () => string | null | undefined;
  /**
   * Default ISO 639-3 language code for discovery requests.
   * Override at runtime via setLanguage().
   */
  defaultLanguage?: string;
}

class DiscoveryPanel extends BaseComponent {
  private readonly gatewayService: GatewayService;
  private onDocumentClick: (doc: unknown) => void;
  private onSelectionChange: (selectedTopicIds: string[]) => void;
  private contextProvider: () => string | null | undefined;
  private currentLanguage: string;
  private toastService: ToastService;
  private abortController?: AbortController;
  private currentResponse: DiscoveryResponse | null = null;
  private selectedTopicIds: Set<string> = new Set();
  private availableSources: string[] = [];
  private selectedSources: Set<string> = new Set();
  private sourcesLoaded: boolean = false;

  constructor(options: DiscoveryPanelOptions) {
    super(options);
    this.gatewayService = options.gatewayService;
    this.onDocumentClick =
      options.onDocumentClick ??
      ((doc) => {
        const url =
          (doc as { url?: string; document_uri?: string })?.url ||
          (doc as { url?: string; document_uri?: string })?.document_uri;
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
      });
    this.onSelectionChange = options.onSelectionChange ?? (() => {});
    this.contextProvider = options.contextProvider ?? defaultContextProvider;
    this.currentLanguage = options.defaultLanguage ?? 'eng';
    this.toastService = ToastService.getInstance();
  }

  protected getDefaultCssClass(): string {
    return 'discovery-panel';
  }

  protected getTranslationFallbacks(): Record<string, string> {
    return {
      'discovery.error': 'Search failed',
      'discovery.disambiguation.title': 'Did you mean...?',
      'discovery.section.empty': 'No results in this section',
    };
  }

  protected override render(): void {
    // Markup lives in index.html; nothing to generate
  }

  protected override onInitialize(): void {
    if (!this.container) return;

    const input =
      this.container.querySelector<HTMLInputElement>('#discovery-input');
    const submit =
      this.container.querySelector<HTMLButtonElement>('#discovery-submit');

    submit?.addEventListener('click', () => this.runQuery(input?.value ?? ''));
    input?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.runQuery(input.value);
      }
    });

    // Populate the source selector from GET_SOURCES. Fire-and-forget;
    // errors degrade silently (selector stays hidden, discover() queries
    // all resolvers by omitting the sources filter).
    void this.fetchSources();
  }

  /**
   * Set the ISO 639-3 language code used for subsequent discovery requests.
   */
  setLanguage(language: string): void {
    this.currentLanguage = language;
  }

  /**
   * The gateway has started (#132). Retries the sources fetch if it hasn't
   * succeeded yet: the initial fetch in onInitialize() runs while components
   * are built, before the gateway is initialized.
   */
  onGatewayReady(): void {
    if (!this.sourcesLoaded) {
      void this.fetchSources();
    }
  }

  /**
   * Submit a discovery query. Cancels any in-flight request first.
   */
  async runQuery(rawQuery: string): Promise<void> {
    const query = rawQuery.trim();
    if (!query) return;

    this.abortController?.abort();
    this.abortController = new AbortController();
    this.showLoading();

    try {
      logger.debug(`DiscoveryPanel: querying "${query}"`);
      const context = this.contextProvider() ?? undefined;
      const sourcesFilter = this.computeSourcesFilter();
      const response = await this.gatewayService.discover(
        query,
        this.currentLanguage,
        context,
        sourcesFilter,
        undefined,
        { signal: this.abortController.signal }
      );
      this.currentResponse = response;
      this.displayResponse(response);
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        return;
      }
      logger.error('DiscoveryPanel: discover failed', error);
      // A 403 from the proxy is shown alone, translated (#113).
      const forbidden = forbiddenMessage(error, (k) => this.t(k));
      if (forbidden) this.toastService.showError(forbidden);
      else this.showError((error as Error).message);
    }
  }

  /**
   * Get the IDs of currently checked topics.
   */
  getSelectedTopicIds(): string[] {
    return Array.from(this.selectedTopicIds);
  }

  /**
   * Uncheck all topic checkboxes and clear the selection set.
   */
  clearSelection(): void {
    this.selectedTopicIds.clear();
    this.container
      ?.querySelectorAll<HTMLInputElement>('[data-discovery-topic-checkbox]')
      .forEach((cb) => {
        cb.checked = false;
      });
    this.onSelectionChange([]);
  }

  // ========== Private rendering helpers ==========

  /**
   * Compute the `sources` argument to pass into `GatewayService.discover()`.
   * Returns `undefined` when the selector is hidden, never been populated,
   * or all sources are checked — in those cases the gateway queries every
   * resolver, which is the same behaviour as passing the full list. Only
   * returns an array when the user has actively narrowed the selection.
   */
  private computeSourcesFilter(): string[] | undefined {
    if (this.availableSources.length === 0) return undefined;
    if (this.selectedSources.size === this.availableSources.length) {
      return undefined;
    }
    return Array.from(this.selectedSources);
  }

  /**
   * Fetch the list of available resolver sources and render the selector.
   * Silent degradation: on any error the selector is hidden and discover()
   * falls back to querying all resolvers (the pre-#62 behaviour).
   */
  private async fetchSources(): Promise<void> {
    try {
      // Gateway may not be initialized yet (components init before it starts).
      // Bail out silently — onGatewayReady() will retry.
      if (!this.gatewayService.isInitialized()) {
        return;
      }
      const sources = await this.gatewayService.getSources(
        this.currentLanguage
      );
      this.sourcesLoaded = true;
      this.availableSources = sources;
      this.selectedSources = new Set(sources);
      this.renderSources();
    } catch (error) {
      logger.error(
        'DiscoveryPanel: getSources failed, source selector disabled',
        error
      );
      this.availableSources = [];
      this.selectedSources.clear();
      this.hideSources();
    }
  }

  private renderSources(): void {
    if (!this.container) return;
    const wrapper =
      this.container.querySelector<HTMLElement>('#discovery-sources');
    const list = this.container.querySelector<HTMLElement>(
      '#discovery-sources-list'
    );
    const countEl = this.container.querySelector<HTMLElement>(
      '#discovery-sources-count'
    );
    if (!wrapper || !list || !countEl) return;

    if (this.availableSources.length === 0) {
      wrapper.classList.add('d-none');
      return;
    }

    list.innerHTML = this.availableSources
      .map(
        (source) => `
          <li class="list-group-item p-1">
            <label class="form-check d-flex align-items-center gap-1 small mb-0">
              <input
                type="checkbox"
                class="form-check-input mt-0"
                data-discovery-source-checkbox
                data-source="${escapeHtml(source)}"
                checked
              />
              <span>${escapeHtml(source)}</span>
            </label>
          </li>
        `
      )
      .join('');

    list
      .querySelectorAll<HTMLInputElement>('[data-discovery-source-checkbox]')
      .forEach((checkbox) => {
        checkbox.addEventListener('change', () => {
          const source = checkbox.getAttribute('data-source');
          if (!source) return;
          if (checkbox.checked) {
            this.selectedSources.add(source);
          } else {
            this.selectedSources.delete(source);
          }
          this.updateSourcesCount();
        });
      });

    this.updateSourcesCount();
    wrapper.classList.remove('d-none');
  }

  private updateSourcesCount(): void {
    if (!this.container) return;
    const countEl = this.container.querySelector<HTMLElement>(
      '#discovery-sources-count'
    );
    if (!countEl) return;
    countEl.textContent = `${this.selectedSources.size}/${this.availableSources.length}`;
  }

  private hideSources(): void {
    if (!this.container) return;
    this.container.querySelector('#discovery-sources')?.classList.add('d-none');
  }

  private showLoading(): void {
    if (!this.container) return;
    this.container
      .querySelector('#discovery-loading')
      ?.classList.remove('d-none');
    this.container
      .querySelector('#discovery-placeholder')
      ?.classList.add('d-none');
    this.container
      .querySelector('#discovery-disambiguation')
      ?.classList.add('d-none');
    this.container.querySelector('#discovery-results')?.classList.add('d-none');
  }

  private showError(message: string): void {
    this.toastService.showError(`${this.t('discovery.error')}: ${message}`);
    if (!this.container) return;
    this.container.querySelector('#discovery-loading')?.classList.add('d-none');
    this.container
      .querySelector('#discovery-placeholder')
      ?.classList.remove('d-none');
  }

  private displayResponse(response: DiscoveryResponse): void {
    if (!this.container) return;

    this.selectedTopicIds.clear();

    this.container.querySelector('#discovery-loading')?.classList.add('d-none');
    this.container
      .querySelector('#discovery-placeholder')
      ?.classList.add('d-none');

    this.renderDisambiguation(response.disambiguation);

    const buckets = this.bucketResults(response);

    const resultsEl =
      this.container.querySelector<HTMLElement>('#discovery-results');
    if (!resultsEl) return;
    resultsEl.classList.remove('d-none');

    for (const section of DISCOVERY_SECTIONS) {
      this.renderSection(resultsEl, section, buckets[section]);
    }
  }

  private bucketResults(
    response: DiscoveryResponse
  ): Record<DiscoverySection, DiscoveryResolverResult[]> {
    const buckets: Record<DiscoverySection, DiscoveryResolverResult[]> = {
      entities: [],
      concepts: [],
      documents: [],
      topics: [],
    };

    // Synthetic 'entities' bucket from top-level entity_id and disambiguation top
    if (response.entity_id || response.disambiguation?.top) {
      const top = response.disambiguation?.top;
      buckets.entities.push({
        source: 'discovery.entity',
        confidence: top?.score ?? 1,
        results: [
          {
            entity_id: response.entity_id ?? top?.entity_id ?? '',
            gloss: top?.gloss ?? null,
            term: response.term,
          },
        ],
      });
    }

    for (const resolverResult of response.results || []) {
      const mapped = DISCOVERY_SOURCE_TO_SECTION[resolverResult.source];
      if (!mapped) {
        logger.warn(
          `DiscoveryPanel: resolver source "${resolverResult.source}" not in ` +
            `DISCOVERY_SOURCE_TO_SECTION; defaulting to "${DISCOVERY_SECTION_DEFAULT}". ` +
            `Edit src/core/app-constants.ts to add an explicit mapping.`
        );
      }
      const section = mapped ?? DISCOVERY_SECTION_DEFAULT;
      buckets[section].push(resolverResult);
    }

    return buckets;
  }

  private renderDisambiguation(
    disambiguation: DiscoveryDisambiguationResult | null
  ): void {
    if (!this.container) return;
    const disambEl = this.container.querySelector<HTMLElement>(
      '#discovery-disambiguation'
    );
    if (!disambEl) return;

    if (!disambiguation || (disambiguation.alternatives?.length ?? 0) === 0) {
      disambEl.classList.add('d-none');
      disambEl.innerHTML = '';
      return;
    }

    const title = escapeHtml(this.t('discovery.disambiguation.title'));
    const top = disambiguation.top;
    const topLabel = escapeHtml(top.gloss || top.entity_id);
    const altsHtml = (disambiguation.alternatives || [])
      .slice(0, 5)
      .map(
        (alt) => `
          <button
            type="button"
            class="btn btn-sm btn-link p-0 me-2 align-baseline"
            data-disambiguation-alt="${escapeHtml(alt.gloss || alt.entity_id)}"
          >${escapeHtml(alt.gloss || alt.entity_id)}</button>
        `
      )
      .join('');

    disambEl.innerHTML = `
      <strong>${title}</strong>
      <div class="mt-1">
        <span class="badge bg-info text-dark me-2">${topLabel}</span>
        ${altsHtml}
      </div>
    `;
    disambEl.classList.remove('d-none');

    disambEl
      .querySelectorAll<HTMLElement>('[data-disambiguation-alt]')
      .forEach((el) => {
        el.addEventListener('click', () => {
          const altQuery = el.getAttribute('data-disambiguation-alt');
          if (altQuery) this.runQuery(altQuery);
        });
      });
  }

  private renderSection(
    resultsEl: HTMLElement,
    section: DiscoverySection,
    sectionResults: DiscoveryResolverResult[]
  ): void {
    const sectionEl = resultsEl.querySelector(`[data-section="${section}"]`);
    const itemsEl = sectionEl?.querySelector<HTMLElement>(
      '.discovery-section-items'
    );
    const countEl = sectionEl?.querySelector<HTMLElement>(
      '.discovery-section-count'
    );
    if (!itemsEl || !countEl) return;

    const flatItems = sectionResults.flatMap((group) =>
      (group.results || []).map((item) => ({ item, source: group.source }))
    );
    countEl.textContent = String(flatItems.length);

    if (flatItems.length === 0) {
      itemsEl.innerHTML = `<li class="list-group-item text-muted small">${escapeHtml(this.t('discovery.section.empty'))}</li>`;
      return;
    }

    itemsEl.innerHTML = flatItems
      .map(({ item, source }) => this.renderResultItem(section, source, item))
      .join('');

    // Wire click handlers for documents and topics
    if (section === 'documents') {
      itemsEl
        .querySelectorAll<HTMLElement>('[data-discovery-document]')
        .forEach((el, idx) => {
          el.addEventListener('click', (e) => {
            e.preventDefault();
            const entry = flatItems[idx];
            if (entry) this.onDocumentClick(entry.item);
          });
        });
    } else if (section === 'topics') {
      itemsEl
        .querySelectorAll<HTMLInputElement>('[data-discovery-topic-checkbox]')
        .forEach((checkbox) => {
          checkbox.addEventListener('change', () => {
            const topicId = checkbox.getAttribute('data-topic-id');
            if (!topicId) return;
            if (checkbox.checked) {
              this.selectedTopicIds.add(topicId);
            } else {
              this.selectedTopicIds.delete(topicId);
            }
            this.onSelectionChange(this.getSelectedTopicIds());
          });
        });
    }
  }

  private renderResultItem(
    section: DiscoverySection,
    source: string,
    item: unknown
  ): string {
    const obj = (item || {}) as Record<string, unknown>;

    if (section === 'documents') {
      const title =
        (obj.short_title as string) ||
        (obj.title as string) ||
        (obj.document_id as string) ||
        (obj.id as string) ||
        'Untitled';
      return `
        <li class="list-group-item p-1">
          <a href="#" data-discovery-document class="text-decoration-none small d-flex align-items-center gap-1">
            ${SVG_DOCUMENT}<span>${escapeHtml(title)}</span>
            <span class="text-muted ms-1">(${escapeHtml(source)})</span>
          </a>
        </li>
      `;
    }

    if (section === 'topics') {
      const topicId =
        (obj.id as string) ||
        (obj.entity_id as string) ||
        (obj.name as string) ||
        '';
      const label =
        (obj.name as string) ||
        (obj.title as string) ||
        (obj.entity_id as string) ||
        (obj.id as string) ||
        String(item);
      return `
        <li class="list-group-item p-1">
          <label class="form-check d-flex align-items-center gap-1 small mb-0">
            <input type="checkbox"
              class="form-check-input mt-0"
              data-discovery-topic-checkbox
              data-topic-id="${escapeHtml(topicId)}"
            />
            ${SVG_TAG}<span>${escapeHtml(label)}</span>
          </label>
        </li>
      `;
    }

    // Entities + Concepts (display-only)
    const label =
      (obj.gloss as string) ||
      (obj.name as string) ||
      (obj.label as string) ||
      (obj.entity_id as string) ||
      (obj.id as string) ||
      String(item);
    const subtext =
      (obj.entity_id as string) && (obj.gloss as string)
        ? ` <code class="text-muted">${escapeHtml(obj.entity_id as string)}</code>`
        : '';
    return `
      <li class="list-group-item p-1 small">
        ${escapeHtml(label)}${subtext}
      </li>
    `;
  }

  override destroy(): void {
    this.abortController?.abort();
    super.destroy();
  }

  override updateUILanguage(): void {
    // Static text uses data-i18n (handled by UIManager).
    // Re-display current results to pick up translated fallback strings.
    if (this.currentResponse) {
      this.displayResponse(this.currentResponse);
    }
  }
}

/**
 * Default contextProvider: returns up to CONTEXT_MAX_CHARS of the currently
 * loaded document's text content. Returns null when no document is loaded
 * (so the gateway can run a context-free disambiguation).
 */
function defaultContextProvider(): string | null {
  const doc = useAppStore.getState().currentDocument;
  if (!doc?.content) return null;
  return doc.content.length > CONTEXT_MAX_CHARS
    ? doc.content.slice(0, CONTEXT_MAX_CHARS)
    : doc.content;
}

export default DiscoveryPanel;
